import { useProfileContext } from '@/context/profileContext';
import { MapType } from '@/types';
import { useCallback, useEffect, useMemo } from 'react';
import {
  getCustomerProperties,
  getScreenProperties,
  getTrafficTrackingProperties,
  pushEventToDataLayer,
} from './useGTM';
import { ECOM_SCREEN_NAMES, EcomGTMEventNames } from '@/types/ecomGTM';
import { AppConfigStateTypes, useAppConfigContext } from '@/context/AppContext';
import { PLATFORMS } from '@/constants/app';
import useWebEngage from './useWebEngage';

type WebEngageEventOptions = {
  fire: boolean;
  isDirect?: boolean;
};

type FireEcomGTMEventOptions = {
  includeCustomer?: boolean;
  includeScreen?: boolean;
  includeSourceCTA?: boolean;
  webengage?: WebEngageEventOptions;
};

const pageTypes: MapType = {
  '/': ECOM_SCREEN_NAMES.HOME,
  '/c/[categoryId]': ECOM_SCREEN_NAMES.CATEGORY_PAGE,
  '/c/[...slug]': ECOM_SCREEN_NAMES.SUBCATEGORY_PAGE,
  '/account/profile': ECOM_SCREEN_NAMES.PROFILE_PAGE,
  '/dp/[...slug]': ECOM_SCREEN_NAMES.PDP_PAGE,
  '/ecom/cart': ECOM_SCREEN_NAMES.CART_PAGE,
  '/ecom/checkout': ECOM_SCREEN_NAMES.CHECKOUT_PAGE,
  '/search': ECOM_SCREEN_NAMES.SEARCH_PAGE,
  '/s/[storeId]': ECOM_SCREEN_NAMES.STORE_PAGE,
  '/ecom/orders': ECOM_SCREEN_NAMES.ORDERS_PAGE,
  '/ecom/order/[orderItemId]': ECOM_SCREEN_NAMES.ORDER_DETAILS_PAGE,
  '/ecom/payment/[orderId]': ECOM_SCREEN_NAMES.PAYMENT_WIDGET_PAGE,
  '/ecom/payment/[...slug]': ECOM_SCREEN_NAMES.ORDER_PAYMENT_STATUS_PAGE,
  '/login': ECOM_SCREEN_NAMES.LOGIN_PAGE,
  '/blog': ECOM_SCREEN_NAMES.BLOG_PAGE,
  '/blog/[blogId]': ECOM_SCREEN_NAMES.BLOG_DETAIL_PAGE,
  '/blog/category/[categoryId]': ECOM_SCREEN_NAMES.BLOG_CATEGORY_PAGE,
};

export const usePageViewed = (pathName: string, appConfig?: AppConfigStateTypes) => {
  const { fireEcomGTMEvent } = useEcomGTM(appConfig);
  const pageType = pageTypes[pathName];
  useEffect(() => {
    if (pageType) {
      fireEcomGTMEvent(EcomGTMEventNames.PAGE_VIEWED, {
        pageType,
      });
    }
  }, [pageType]);
};

const useEcomGTM = (appConfig?: AppConfigStateTypes) => {
  const { state: profile } = useProfileContext();
  const { state: appConfigContext } = useAppConfigContext();
  const appConfiguration: AppConfigStateTypes = appConfig ?? appConfigContext;
  const { fireWebEngageEvent } = useWebEngage();
  const customerProperties = useMemo(() => getCustomerProperties(profile), [profile]);
  const fireEcomGTMEvent = useCallback(
    (event: string, properties?: MapType, options?: FireEcomGTMEventOptions) => {
      const {
        includeCustomer = true,
        includeScreen = true,
        includeSourceCTA = true,
        webengage = { fire: false, isDirect: false },
      } = options ?? {};
      let eventMeta = Object.assign({ event }, properties ?? {});
      if (includeScreen) {
        eventMeta = Object.assign(eventMeta, getScreenProperties());
      }

      if (includeCustomer) {
        eventMeta = Object.assign(eventMeta, customerProperties);
      }
      eventMeta = Object.assign(eventMeta, getTrafficTrackingProperties());
      eventMeta['platform'] = appConfiguration.isFlutterClient
        ? PLATFORMS.FLUTTER_WEB_VIEW
        : PLATFORMS.WEB;
      pushEventToDataLayer(eventMeta);
      if (options?.webengage?.fire) {
        fireWebEngageEvent(eventMeta, { isDirect: webengage.isDirect });
      }
    },
    [customerProperties, appConfiguration.isFlutterClient, fireWebEngageEvent],
  );

  return { fireEcomGTMEvent };
};

export default useEcomGTM;
